import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiBookOpen, FiClock, FiTarget, FiAlertCircle, FiRefreshCw, FiTrendingUp, FiAward, FiCalendar } from 'react-icons/fi';
import { format } from 'date-fns';
import StatsCard from '../components/StatsCard';
import ConsistencyHeatmap from '../components/Charts/ConsistencyHeatmap';
import DifficultyDonut from '../components/Charts/DifficultyDonut';
import CategoryProgress from '../components/Charts/CategoryProgress';
import TimeAnalysis from '../components/Charts/TimeAnalysis';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentProblems, setRecentProblems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refresh data when user navigates back to dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      fetchDashboardData();
    }
  }, [location.pathname]);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const [statsRes, problemsRes] = await Promise.all([
        axios.get('/api/problems/stats'),
        axios.get('/api/problems?limit=5&sortBy=solveDate&sortOrder=desc')
      ]);
      
      setStats(statsRes.data.stats);
      setRecentProblems(problemsRes.data.problems);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const getDifficultyConfig = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return {
          color: 'bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
          text: 'text-green-700 dark:text-green-300',
          border: 'border-green-200 dark:border-green-700',
        };
      case 'Medium':
        return {
          color: 'bg-gradient-to-r from-yellow-100 to-orange-200 dark:from-yellow-900/30 dark:to-orange-800/30',
          text: 'text-orange-700 dark:text-orange-300',
          border: 'border-orange-200 dark:border-orange-700',
        };
      case 'Hard':
        return {
          color: 'bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
          text: 'text-red-700 dark:text-red-300',
          border: 'border-red-200 dark:border-red-700',
        };
      default:
        return {
          color: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
          border: 'border-gray-200 dark:border-gray-600',
        };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Solved':
        return {
          color: 'bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30',
          text: 'text-primary-700 dark:text-primary-300',
          border: 'border-primary-200 dark:border-primary-700',
        };
      case 'In Progress':
        return {
          color: 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30',
          text: 'text-blue-700 dark:text-blue-300',
          border: 'border-blue-200 dark:border-blue-700',
        };
      case 'For Review':
        return {
          color: 'bg-gradient-to-r from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30',
          text: 'text-purple-700 dark:text-purple-300',
          border: 'border-purple-200 dark:border-purple-700',
        };
      default:
        return {
          color: 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700',
          text: 'text-gray-700 dark:text-gray-300',
          border: 'border-gray-200 dark:border-gray-600',
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-gray-200 dark:border-gray-700"></div>
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary-600 border-t-transparent absolute top-0 left-0"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-primary-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <FiAlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Unable to Load Dashboard
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              We encountered an issue loading your dashboard data
            </p>
            <button
              onClick={handleRefresh}
              className="btn-primary inline-flex items-center"
            >
              <FiRefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-900/20">
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Enhanced Header */}
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Coding Dashboard
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-6">
            Track your progress, maintain consistency, and level up your coding skills
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`px-6 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 inline-flex items-center ${refreshing ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
            
            <Link
              to="/problems/add"
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 inline-flex items-center"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Add Problem
            </Link>
          </div>
        </div>

        {/* Enhanced Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            <StatsCard
              title="Total Problems"
              value={stats.totalProblems}
              icon={FiBookOpen}
              color="purple"
              subtitle="all time"
            />
          </div>
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" style={{ animationDelay: '100ms' }}>
            <StatsCard
              title="This Week"
              value={stats.problemsThisWeek}
              icon={FiTrendingUp}
              color="blue"
              subtitle="problems solved"
            />
          </div>
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" style={{ animationDelay: '200ms' }}>
            <StatsCard
              title="Current Streak"
              value={stats.currentStreak}
              currentStreak={stats.currentStreak}
              icon={FiTarget}
              color="green"
              subtitle="days in a row"
            />
          </div>
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" style={{ animationDelay: '300ms' }}>
            <StatsCard
              title="Avg Solve Time"
              value={`${stats.averageSolveTime}m`}
              icon={FiClock}
              color="yellow"
              subtitle="per problem"
            />
          </div>
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" style={{ animationDelay: '400ms' }}>
            <StatsCard
              title="Pending Revisions"
              value={stats.pendingRevisions}
              icon={FiAlertCircle}
              color="red"
              subtitle="due today"
            />
          </div>
        </div>

        {/* Enhanced Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Consistency Heatmap */}
          <div className="lg:col-span-2 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '500ms' }}>
            <ConsistencyHeatmap data={stats.heatmapData} />
          </div>

          {/* Difficulty Distribution */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '600ms' }}>
            <DifficultyDonut data={stats.difficultyStats} />
          </div>

          {/* Category Progress */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '700ms' }}>
            <CategoryProgress data={stats.categoryStats} />
          </div>

          {/* Time Analysis */}
          <div className="lg:col-span-2 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '800ms' }}>
            <TimeAnalysis data={stats.timeAnalysis} />
          </div>
        </div>

        {/* Enhanced Recent Activity */}
        <div className="card animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '900ms' }}>
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Recent Activity
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Your latest problem-solving sessions
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {recentProblems.length} Recent
                </span>
              </div>
            </div>
          </div>
          
          <div className="card-body">
            {recentProblems.length > 0 ? (
              <div className="space-y-4">
                {recentProblems.map((problem, index) => {
                  const difficultyConfig = getDifficultyConfig(problem.platformDifficulty);
                  const statusConfig = getStatusConfig(problem.status);
                  
                  return (
                    <div
                      key={problem._id}
                      className="group relative p-6 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/80 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className={`inline-flex items-center px-3 py-1 rounded-xl border text-xs font-semibold ${difficultyConfig.color} ${difficultyConfig.text} ${difficultyConfig.border}`}>
                              {problem.platformDifficulty}
                            </div>
                            <div className={`inline-flex items-center px-3 py-1 rounded-xl border text-xs font-semibold ${statusConfig.color} ${statusConfig.text} ${statusConfig.border}`}>
                              {problem.status}
                            </div>
                          </div>
                          
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-700 dark:group-hover:text-primary-300 transition-colors duration-200">
                            {problem.problemName}
                          </h4>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center space-x-1">
                              <FiBookOpen className="w-4 h-4" />
                              <span>{problem.platform}</span>
                            </div>
                            <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                            <div className="flex items-center space-x-1">
                              <FiTarget className="w-4 h-4" />
                              <span>{problem.mainCategory}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right ml-6">
                          <div className="flex items-center space-x-2 mb-2">
                            <FiClock className="w-4 h-4 text-gray-400" />
                            <span className="text-lg font-bold text-gradient">
                              {problem.timeTaken}m
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                            <FiCalendar className="w-3 h-3" />
                            <span>{format(new Date(problem.solveDate), 'MMM dd, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hover Effect Progress Bar */}
                      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300 group-hover:w-full w-0 rounded-b-2xl" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="relative mx-auto mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-3xl flex items-center justify-center shadow-xl">
                    <FiBookOpen className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
                    <FiAward className="w-4 h-4 text-white" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Ready to Start Coding?
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                  Begin your coding journey by adding your first problem. Track your progress and build consistent habits!
                </p>
                
                <Link
                  to="/problems/add"
                  className="px-8 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 inline-flex items-center"
                >
                  <FiPlus className="w-5 h-5 mr-2" />
                  Add Your First Problem
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;